import { Immutable } from "immer";
import React from "react";
import SVG from "react-inlinesvg";

import iconPhone from "../../../../img/icons/phone.svg";
import iconPin from "../../../../img/icons/pin.svg";
import iconTurn from "../../../../img/icons/turn.svg";
import { Image } from "../../../components/Image";
import { ILocation, RetailStore } from "../../../models/location";
import { WebPageURL, isoPostcode, isoStateA2 } from "../../../models/nominals";
import { formatPhoneNumber, getStoreAddressParts } from "../../../utils/format";
import { usePageSetting } from "../../../utils/settings";
import { formatDistanceWithSuffix } from "../helpers";

import styles from "./RetailCardsContent.module.scss";

const RetailCard = (props: Immutable<{ store: RetailStore }>) => {
    const googleMapsApiKey = usePageSetting("google_maps_api_key");
    // encode destination address for Directions link
    const destinationAddress = encodeURI(
        props.store.address +
            " " +
            props.store.address2 +
            " " +
            props.store.city +
            "," +
            isoStateA2.unwrap(props.store.state) +
            " " +
            isoPostcode.unwrap(props.store.postal),
    );
    const addressParts = getStoreAddressParts(props.store);

    return (
        <div className={styles.card}>
            <header>
                <Image
                    alt="Gallery Image 1"
                    src={`https://maps.googleapis.com/maps/api/staticmap?center=${
                        props.store.lat
                    },${props.store.lng}
                         &zoom=15
                         &size=400x300
                         &markers=${props.store.lat},${props.store.lng}
                         &key=${googleMapsApiKey}`}
                />
                <p>
                    <em>
                        <strong>
                            {props.store.distance &&
                                formatDistanceWithSuffix(props.store.distance)}
                        </strong>
                    </em>
                </p>
            </header>
            <SVG className="" src={iconPin} />
            <h4>{props.store.name}</h4>
            <p>
                <span>{addressParts.streetLine1}</span>
                {addressParts.streetLine2 && (
                    <>
                        <br />
                        <span>{addressParts.streetLine2}</span>
                    </>
                )}
                <br />
                <span>{addressParts.cityStatePostal}</span>
            </p>
            {props.store.phone && <SVG className="" src={iconPhone} />}
            {props.store.phone && (
                <a href={`tel:${props.store.phone}`}>
                    {formatPhoneNumber(props.store.phone)}
                </a>
            )}
            <nav>
                <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=${destinationAddress}`}
                    target="_blank"
                    className="button caption"
                    rel="noreferrer"
                >
                    <SVG className="" src={iconTurn} />
                    <span>Directions</span>
                </a>
            </nav>
        </div>
    );
};

export const RetailCardsBlock = (
    props: Immutable<{
        stores: ReadonlyArray<RetailStore>;
        location: ILocation | null;
        retailLocatorLink: WebPageURL;
    }>,
) => {
    return (
        <div className={styles.retailCardsContainer}>
            <div className={styles.retailCards}>
                {props.location && (
                    <>
                        <h2>Closest retailers near</h2>
                        <a href={`${props.retailLocatorLink}`}>
                            {props.location.formatted_address}
                        </a>
                    </>
                )}
                <div>
                    {props.stores.slice(0, 4).map((store) => (
                        <RetailCard
                            {...props}
                            key={`${store.id}`}
                            store={store}
                        />
                    ))}
                </div>
                <a
                    href={`${props.retailLocatorLink}`}
                    className="button --secondary"
                >
                    View all retailers
                </a>
            </div>
        </div>
    );
};
